import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, getStoredToken, setStoredToken } from '../api/client';
import type { User } from '@shared/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  loginWithGoogleCredential: (credential: string) => Promise<void>;
  loginWithGoogleCode: (code: string, redirectUri?: string) => Promise<void>;
  loginDemo: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await api.logout().catch(() => {});
      }
    } finally {
      setStoredToken(null);
      setToken(null);
      setUser(null);
    }
  }, [token]);

  const loginWithGoogleCredential = async (credential: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.googleAuth({ credential });
      setStoredToken(res.token);
      setToken(res.token);
      setUser(res.user);
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogleCode = async (code: string, redirectUri?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.googleAuth({ code, redirect_uri: redirectUri });
      setStoredToken(res.token);
      setToken(res.token);
      setUser(res.user);
    } catch (err: any) {
      setError(err.message || 'Google OAuth code exchange failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginDemo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.googleAuth({ is_demo: true });
      setStoredToken(res.token);
      setToken(res.token);
      setUser(res.user);
    } catch (err: any) {
      setError(err.message || 'Demo login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Check existing token or OAuth code in URL on initial mount
  useEffect(() => {
    async function initAuth() {
      // 1. Check if OAuth redirect returned a code
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');

      if (authCode) {
        try {
          const origin = window.location.origin;
          const redirectUri = `${origin}/auth/callback`;
          const res = await api.googleAuth({ code: authCode, redirect_uri: redirectUri });
          setStoredToken(res.token);
          setToken(res.token);
          setUser(res.user);

          // Clean URL without reloading
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          setIsLoading(false);
          return;
        } catch (err: any) {
          setError(`OAuth authentication error: ${err.message}`);
        }
      }

      // 2. Check existing token
      const existingToken = getStoredToken();
      if (existingToken) {
        try {
          const profile = await api.getMe();
          setUser(profile);
          setToken(existingToken);
        } catch (err) {
          // Token expired or invalid
          setStoredToken(null);
          setToken(null);
          setUser(null);
        }
      }

      setIsLoading(false);
    }

    initAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        error,
        loginWithGoogleCredential,
        loginWithGoogleCode,
        loginDemo,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
